import type { AccessDeniedReason, SessionIntent } from './types';

/**
 * Event vocabulary as a name → properties map. Adding a new provider does
 * NOT require new event types; provider is always a property.
 *
 * Naming follows Segment's "Object Verb (past tense)" convention.
 *
 * We use a map (not a discriminated union) because the union grows the type
 * graph quadratically with consumers like JSX, triggering TS2590.
 */
export interface EventCatalog {
    'Signed In': { is_first_login: boolean; session_intent: SessionIntent; days_since_last_session?: number; app_version?: string };
    'Access Denied': { reason: AccessDeniedReason | string };
    'Agent Selected': { agent_id: string; agent_name?: string; entry_point?: string };
    'Assistant Session Started': { agent_id: string; session_id?: string };
    'Assistant Message Sent': { agent_id: string; session_id?: string; message_length: number; turn_index: number };
    'Best Practice Viewed': { category: string; view_name: string };
    'Optimization Check Run': { check_id?: string; scope?: string; result_count?: number };
    'Rule Summary Viewed': { rule_id?: string; source?: string };
    'User Settings Updated': { fields_changed: string[] };
}

export type EventName = keyof EventCatalog;
export type PropertiesFor<N extends EventName> = EventCatalog[N];

export interface AnalyticsEvent<N extends EventName = EventName> {
    name: N;
    properties: EventCatalog[N];
}

/**
 * Typed factory for analytics events. Properties are validated against the
 * EventCatalog at construction, not at the track() call site — this keeps
 * call-site inference cheap and avoids TS2590 in JSX-heavy components.
 *
 * Usage: analytics.track(event('Signed In', { ...props }))
 */
export const event = <N extends EventName>(
    name: N,
    properties: EventCatalog[N],
): { name: N; properties: EventCatalog[N] } => ({ name, properties });
